/* ----------------- Globals --------------- */
import { events } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import { useAppTheme } from "@hooks/useAppTheme";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

/* ----------------- Components --------------- */
import { Text } from "react-native-paper";

import Box from "@memorang/ui/components/Box";
import { TextSkeleton } from "@memorang/ui/components/ChatUI/TextSkeleton";
import HintAccordionListItem from "@memorang/ui/components/HintAccordionListItem";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
/* ----------------- Types --------------- */
import type { UseHintReturnType } from "../hooks/useHint";
import { AIFeedbackMenu } from "./AIFeedbackMenu";

type Props = UseHintReturnType & {
	itemId: number;
};

export const HintAccordion = ({
	itemId,
	getHint,
	hints,
	loading,
	streaming,
}: Props) => {
	const [showHint, setShowHint] = useState(false);
	const theme = useAppTheme();
	const { t } = useTranslation();

	const lastHint = useMemo(() => {
		return hints[hints.length - 1]?.text;
	}, [hints.length, hints[hints.length - 1]?.text]);

	useAnalytics(
		{
			eventName: events.sessionItemHintGiven,
			itemId: `${itemId}`,
			hint: lastHint ?? "",
		},
		showHint && !loading && !streaming,
	);

	const handleToggle = () => {
		setShowHint((prev) => !prev);
		getHint();
	};

	const color = showHint ? theme.colors.text : theme.colors.primary;

	const title = useMemo(() => {
		if (!showHint) {
			return (
				<Text
					variant="bodyMedium"
					style={{
						fontWeight: "bold",
						color,
					}}
				>
					{t("session.hint.show")}
				</Text>
			);
		}

		if (loading || !lastHint) {
			return <TextSkeleton height={14} numLines={1} />;
		}

		return (
			<Box flexDirection="row" justifyContent="space-between">
				<Box flex={1}>
					<CustomMarkdownRenderer
						text={lastHint}
						variant="answer"
						customStyles={{
							text: {
								fontSize: 16,
								lineHeight: 24,
								color,
							},
						}}
					/>
				</Box>
				<Box paddingHorizontal={8}>
					<AIFeedbackMenu
						itemId={itemId}
						featureName="AI Hint"
						disabled={streaming}
					/>
				</Box>
			</Box>
		);
	}, [lastHint, loading, showHint, color, t, itemId, streaming]);

	return (
		<HintAccordionListItem
			showHint={showHint}
			title={title}
			handleToggle={handleToggle}
			isLastHint={Boolean(lastHint?.length)}
		/>
	);
};
